import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Building2, User } from 'lucide-react';
import Step1ClientInfo from '../components/steps/Step1ClientInfo';
import Step2Validation from '../components/steps/Step2Validation';
import Step3Video from '../components/steps/Step3Video';
import Step4Signature from '../components/steps/Step4Signature';
import Step5Avis from '../components/steps/Step5Avis';
import Step6Confirmation from '../components/steps/Step6Confirmation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FormWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [userInfo, setUserInfo] = useState(null);
  const [branding, setBranding] = useState(null);
  const [formData, setFormData] = useState({
    clientInfo: null,
    formValidation: null,
    videoData: null,
    signatureData: null,
    avisData: null,
  });

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    const info = localStorage.getItem('user_info');
    if (!token || !info) {
      navigate('/login');
      return;
    }
    const parsedInfo = JSON.parse(info);
    setUserInfo(parsedInfo);
    
    // Load branding from localStorage or fetch it
    const storedBranding = localStorage.getItem('company_branding');
    if (storedBranding) {
      setBranding(JSON.parse(storedBranding));
    } else if (parsedInfo.company_id) {
      // Fetch branding if not in localStorage
      fetchCompanyBranding(parsedInfo.company_id);
    }
  }, [navigate]);

  const fetchCompanyBranding = async (companyId) => {
    try {
      const res = await fetch(`${API_URL}/api/company/branding-by-id/${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setBranding(data);
        localStorage.setItem('company_branding', JSON.stringify(data));
      }
    } catch (e) {
      console.warn('Could not fetch company branding');
    }
  };

  const steps = [
    { number: 1, title: 'Informations', component: Step1ClientInfo },
    { number: 2, title: 'Validation', component: Step2Validation },
    { number: 3, title: 'Vidéo', component: Step3Video },
    { number: 4, title: 'Signature', component: Step4Signature },
    { number: 5, title: 'Avis', component: Step5Avis },
    { number: 6, title: 'Confirmation', component: Step6Confirmation },
  ];

  const handleNext = (stepData) => {
    const stepKey = Object.keys(formData)[currentStep - 1];
    setFormData({ ...formData, [stepKey]: stepData });
    setCurrentStep(currentStep + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    } else {
      navigate('/');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('company_branding');
    navigate('/login');
  };

  if (!userInfo) return null;

  const CurrentStepComponent = steps[currentStep - 1].component;
  
  // Get branding colors with defaults
  const b = branding?.branding || {};
  const primaryColor = b.primary_color || '#f97316';
  const headerColor = b.header_color || '#ffffff';
  const bgColor = b.background_color || '#f8fafc';
  const logoUrl = b.logo_url ? (b.logo_url.startsWith('/') ? `${API_URL}${b.logo_url}` : b.logo_url) : null;
  const companyTitle = b.title || branding?.company_name || userInfo.company_name;

  // CSS custom properties for branding
  const brandingStyles = {
    '--brand-primary': primaryColor,
    '--brand-primary-light': primaryColor + '15',
    '--brand-header': headerColor,
    '--brand-bg': bgColor,
    backgroundColor: bgColor,
  };

  return (
    <div className="min-h-screen flex flex-col branded-form" style={brandingStyles}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 h-16 border-b backdrop-blur flex items-center justify-between px-4"
        style={{ backgroundColor: headerColor + 'f5' }}
      >
        <button
          data-testid="back-btn"
          onClick={handleBack}
          className="flex items-center gap-2 transition-colors"
          style={{ color: primaryColor }}
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
          <span>Retour</span>
        </button>
        
        {/* Logo and Company Name */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
          {logoUrl && (
            <img src={logoUrl} alt="Logo" className="h-8 w-8 object-contain rounded" />
          )}
          {companyTitle && (
            <span className="hidden sm:block font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {companyTitle}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-flex items-center gap-1 text-xs text-foreground/60">
            <User className="w-3 h-3" />
            {userInfo.name}
          </span>
          <div className="text-sm text-foreground/60">
            {currentStep}/6
          </div>
          <button
            data-testid="user-logout-btn"
            onClick={handleLogout}
            className="text-xs text-foreground/50 hover:text-destructive transition-colors"
          >
            Quitter
          </button>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="sticky top-16 z-40 backdrop-blur border-b px-4 py-3" style={{ backgroundColor: headerColor + 'f5' }}>
        <div className="max-w-md mx-auto">
          <div className="flex gap-2">
            {steps.map((step) => (
              <div
                key={step.number}
                className="h-1 flex-1 rounded-full transition-all"
                style={{ 
                  backgroundColor: step.number <= currentStep ? primaryColor : '#e2e8f0'
                }}
                data-testid={`progress-step-${step.number}`}
              />
            ))}
          </div>
          <p className="text-sm text-foreground/80 mt-2 font-medium">
            {steps[currentStep - 1].title}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-4 pb-24">
        <div className="max-w-md mx-auto">
          <CurrentStepComponent
            data={formData[Object.keys(formData)[currentStep - 1]]}
            onNext={handleNext}
            onBack={handleBack}
            allData={formData}
            userInfo={userInfo}
            branding={{ primaryColor, bgColor, headerColor, logoUrl, companyTitle }}
          />
        </div>
      </main>
    </div>
  );
}